import * as IoC from 'typescript-ioc';
import { action, observable } from 'mobx';
import { alertServerError } from '../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { IBlueprintsDAO, IMeetingTemplateDTO } from '../../../api/blueprints/blueprints.dao';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';
import { IntlShape } from 'react-intl';
import { MeetingTemplatesForm } from './meeting-template-form/meeting-template-form';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../infrastructure/utils/blueprint-details.mapper';
import { IBlueprintItemDetails } from '../../../api/blueprints/blueprint-details.types';
import { TObject } from '../../../infrastructure/type-extensions';
import { IConnectionTemplateDTO } from '../../../api/blueprints/connection.template.types';

export class MeetingTemplatesVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @observable
    private templates: IMeetingTemplateDTO[] = [];
    @observable
    public connectionsTemplates: IConnectionTemplateDTO[] = [];
    @observable
    public isLoading: boolean = false;
    @observable
    public selectedTemplate: IBlueprintItemDetails;
    @observable
    private blueprintIdToEditPopupMap: TObject<ISubWindowFacade> = {};

    constructor(
        private readonly accountId: number,
        private readonly intlShape: IntlShape,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) { }

    public get meetingTemplates(): IMeetingTemplateDTO[] {
        return this.templates;
    }

    public get intl(): IntlShape { return this.intlShape; }

    @action.bound
    public async initialize(): Promise<void> {
        this.startLoading();

        try {
            const params = { accountId: this.accountId };

            const [blueprintsResponse, templatesResponse] = await Promise.all([
                this.blueprintsDAO.getMeetingTemplateList(params),
                this.blueprintsDAO.getConnectionTemplateList(params)
            ]);

            this.setBlueprints(blueprintsResponse);
            this.connectionsTemplates = templatesResponse;
        } catch (error) {
            MeetingTemplatesVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    public openCreateTemplateForm(): void {
        this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: 'oe.blueprints.meeting.create.title',
                defaultMessage: 'New Meeting Template',
                description: 'Popover header text'
            }),
            componentToHost: MeetingTemplatesForm,
            componentToHostProps: {
                accountId: this.accountId,
                apiCallSuccessCallback: () => {
                    this.initialize();
                }
            }
        });
    }

    public openEditTemplateForm(blueprintId: string): ISubWindowFacade {
        return this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: 'oe.blueprints.meeting.edit.title',
                defaultMessage: 'Edit Meeting Template',
                description: 'Popover header text'
            }),
            componentToHost: MeetingTemplatesForm,
            componentToHostProps: {
                accountId: this.accountId,
                intl: this.intl,
                blueprintId: blueprintId,
                apiCallSuccessCallback: () => {
                    this.initialize();
                },
                deleteCompletionCallback: () => this.completeMeetingTemplateDeletion(blueprintId)
            }
        });
    }

    @action.bound
    private setBlueprints(meetingTemplates: IMeetingTemplateDTO[]): void {
        this.templates = meetingTemplates
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    @action.bound
    private startLoading(): void {
        this.isLoading = true;
    }
    @action.bound
    private stopLoading(): void {
        this.isLoading = false;
    }

    public isTemplateActive = (blueprintId: string): boolean => {
        return this.selectedTemplate?.blueprintId === blueprintId;
    };

    public isTemplateSelected = (blueprintId: string): boolean => {
        return !this.isEditFormHidden(blueprintId) || this.isTemplateActive(blueprintId);
    };

    @action
    public editConnectionTemplate = (blueprintId: string) => {
        if (this.isEditFormHidden(blueprintId)) {
            this.blueprintIdToEditPopupMap[blueprintId] = this.openEditTemplateForm(blueprintId);
        }
    };

    public isEditFormHidden = (blueprintId: string) => {
        const instance = this.blueprintIdToEditPopupMap[blueprintId];
        return !instance || instance.getStatus() === SubWindowStatus.Closed;
    };

    private completeMeetingTemplateDeletion = (deletedBlueprintId: string): void => {
        this.closeEditForm(deletedBlueprintId);
        this.initialize();
    };

    private closeEditForm(blueprintId: string): void {
        this.blueprintIdToEditPopupMap[blueprintId].close();
    }

    @action
    public openMeetingTemplateDetails = (blueprintId: string): void => {
        const template = this.templates.find(t => t.blueprintId === blueprintId);
        this.selectedTemplate = convertBlueprintTemplateIntoBlueprintDetails(
            template.type,
            template,
            null,
            null,
            this.connectionsTemplates
        );
    };

    @action
    public closeBluePrintItemDetailsPopover = (): void => {
        this.selectedTemplate = undefined;
    };

}
